import {
	Component, Input, Output, OnInit, OnDestroy, OnChanges, SimpleChanges, SimpleChange,
	EventEmitter, ChangeDetectorRef, ViewChild, ElementRef, AfterViewInit
} from '@angular/core';
import { Observable, Subject, Subscription } from 'rxjs/Rx';
import { UntypedFormBuilder, UntypedFormGroup, UntypedFormControl, Validators, ValidatorFn } from '@angular/forms';
import { UsersService } from '../../services/users.service';
import { MessageService } from 'base';
import { SamlSettingsDto } from 'UvExplorerServer.Dtos.TS/Auth';
import { ObjSerUtils } from 'DiscoveryEngine.TS/Serialization';
import { IdentityService } from 'identity';
import { ComponentBase } from '../../../common';


@Component({
	selector: 'saml-auth-settings',
	templateUrl: './saml-auth-settings.component.html',
	styleUrls: ['./saml-auth-settings.component.scss']
})
export class SamlAuthSettingsComponent extends ComponentBase implements OnInit, OnDestroy, OnChanges {

	public form: UntypedFormGroup;

	public samlEnabled: UntypedFormControl;
	public appEntityId: UntypedFormControl;
	public providerId: UntypedFormControl;
	public providerLoginUrl: UntypedFormControl;
	public providerLogoutUrl: UntypedFormControl;
	public providerCertificate: UntypedFormControl;
	
	private samlSettingsLoadedSub: Subscription;
	private samlSettings: SamlSettingsDto;

	constructor(private fb: UntypedFormBuilder,
		private usersService: UsersService,
		private messageService: MessageService) {

		super();

		this.samlSettings = null;

		this.samlSettingsLoadedSub = this.usersService.onSamlSettingsLoaded.subscribe(
			(result: SamlSettingsDto) => {
				this.copySamlSettings();
			});
	}

	private copySamlSettings(): void {
		if (this.usersService.hasSamlSettings) {
			this.samlSettings = new SamlSettingsDto();
			ObjSerUtils.Copy(this.usersService.samlSettings, this.samlSettings);
		}
		else {
			this.samlSettings = null;

			const errorMessage = "Could not retrieve authentication settings";
			this.messageService.displayErrorMessage(errorMessage);
		}

		this.buildForm();
	}

	public ngOnInit(): void {

		this.buildForm();

		if (this.usersService.hasSamlSettings) {
			this.copySamlSettings();
		}
	}

	public ngOnDestroy(): void {
		if (this.samlSettingsLoadedSub) {
			this.samlSettingsLoadedSub.unsubscribe();
			this.samlSettingsLoadedSub = null;
		}
	}

	public ngOnChanges(changes: SimpleChanges): void {

	}

	private buildForm(): void {
		let samlEnabledValue = false;
		let appEntityIdValue = "";
		let providerIdValue = "";
		let providerLoginUrlValue = "";
		let providerLogoutUrlValue = "";
		let providerCertificateValue = "";

		if (!!this.samlSettings) {
			samlEnabledValue = this.samlSettings.Enabled;
			appEntityIdValue = this.samlSettings.AppEntityId;
			providerIdValue = this.samlSettings.ProviderId;
			providerLoginUrlValue = this.samlSettings.ProviderLoginUrl;
			providerLogoutUrlValue = this.samlSettings.ProviderLogoutUrl;
			providerCertificateValue = this.samlSettings.ProviderCertificate;
		}

		this.samlEnabled = new UntypedFormControl(samlEnabledValue);
		this.appEntityId = new UntypedFormControl(appEntityIdValue, Validators.required);
		this.providerId = new UntypedFormControl(providerIdValue, Validators.required);
		this.providerLoginUrl = new UntypedFormControl(providerLoginUrlValue, Validators.required);
		this.providerLogoutUrl = new UntypedFormControl(providerLogoutUrlValue, Validators.required);
		this.providerCertificate = new UntypedFormControl(providerCertificateValue, Validators.required);

		this.form = this.fb.group({
			samlEnabled: this.samlEnabled,
			appEntityId: this.appEntityId,
			providerId: this.providerId,
			providerLoginUrl: this.providerLoginUrl,
			providerLogoutUrl: this.providerLogoutUrl,
			providerCertificate: this.providerCertificate,
		});

		setTimeout(() => this.enableDisable(), 0);
	}

	public checkChanged(): void {
		this.enableDisable();
	}

	private enableDisable(): void {
		if (this.samlEnabled.value) {
			this.appEntityId.enable();
			this.providerId.enable();
			this.providerLoginUrl.enable();
			this.providerLogoutUrl.enable();
			this.providerCertificate.enable();
		}
		else {
			this.appEntityId.disable();
			this.providerId.disable();
			this.providerLoginUrl.disable();
			this.providerLogoutUrl.disable();
			this.providerCertificate.disable();
		}
	}

	public enableSave(): boolean {
		return (!!this.samlSettings && this.form.dirty && this.form.valid);
	}

	public onSave(): void {
		this.getSettings();
		this.save();
	}

	private save(): void {
		this.getSettings();

		this.messageService.displayInfoMessage("Saving authentication settings");
		this.messageService.startWork();
		this.usersService.setAuthSettings(null, null, this.samlSettings).subscribe(
			(result: boolean) => {
				this.messageService.endWork();
				this.messageService.displayInfoMessage("Authentication settings were saved");
				this.buildForm();
			},
			(error) => {
				this.messageService.endWork();
				this.messageService.displayErrorMessage("Could not save authentication settings");
			});
	}

	private getSettings(): void {
		this.samlSettings.Enabled = this.samlEnabled.value;
		this.samlSettings.AppEntityId = this.appEntityId.value;
		this.samlSettings.ProviderId = this.providerId.value;
		this.samlSettings.ProviderLoginUrl = this.providerLoginUrl.value;
		this.samlSettings.ProviderLogoutUrl = this.providerLogoutUrl.value;
		this.samlSettings.ProviderCertificate = this.providerCertificate.value;
	}

}
